/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes, Modal,
    ModalBody, ModalHeader, onTelemetryClick, TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import {  WishListIconComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICittaHeaderData } from './citta-header.data';
import { ICittaHeaderProps, ICittaHeaderResources, IMyAccountLinksData } from './citta-header.props.autogenerated';
import { HeaderPreferredStore, Logo, NavIcon } from './components';
import { CartIconComponent } from '../../themes/citta/views/components/cart-icon.component';


export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
}

export interface IHeaderViewProps extends ICittaHeaderProps<ICittaHeaderData>, IHeaderState {
    logo: React.ReactNode;
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    navIcon: React.ReactNode;
    className: string;
    menuBar: React.ReactNode[];
    search: React.ReactNode[];
    siteOptions: React.ReactNode[];
    categoryMenuBar: React.ReactNode[];
    customNavBar: React.ReactNode[];
    countryPicker: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;
    HeaderTopBarRightContainer: INodeProps;
    Divider: INodeProps;

    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinks?: React.ReactNode[];
    preferredStore?: React.ReactNode;
}

/**
 *
 * Header component
 * @extends {React.PureComponent<ICittaHeaderProps<ICittaHeaderData>>}
 */
@observer
class Header extends React.PureComponent<ICittaHeaderProps<ICittaHeaderData>, IHeaderState> {
    private popOverRef: React.RefObject<HTMLButtonElement>;
    private telemetryContent: ITelemetryContent;
    private cart: ICartState | undefined;
    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        const isB2B = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
        if(isB2B){
        return customer ? customer.Name || customer.FirstName : undefined;
        }
        return customer ? customer.FirstName || customer.Name : undefined;
    }
    @computed public get accountEmailAddress(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.Email || customer.Email : undefined;
    }

    constructor(props: ICittaHeaderProps<ICittaHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('keydown', this._keydown);
        }
        reaction(
            () => this.props.data.cart.result,
            () => {
               this.cart = this.props.data.cart.result;
            }
        );

        this._stickyHeaderSetup();
    }

    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('keydown', this._keydown, false);
        }
    }

    // tslint:disable-next-line: cyclomatic-complexity max-func-body-length
    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: { accountInformation,
                storeSelectorStateManager: {result: storeSelectorStateManager}
            },
            context: {
                request: {
                    user
                }
            },
            context,
            resources: {
                cartLabel
            }
        } = this.props;        

        const headerClassName = classnames('ms-header', this.props.config.className);
        const navbarKey = 'header-nav-mobile';
        const customer = accountInformation && accountInformation.result;
        const cartIconSlot = this._getSlot('cartIcon');
        const menuBarSlot = this._getSlot('menuBar');
        const searchSlot = this._getSlot('search');
        const siteOptionsSlot = this._getSlot('siteOptions');
        const preferredStoreSlot = this._getSlot('storeSelector');

        const categoryMenuBarSlot = this._getSlot('categoryMenuBar');
        const customNavBarSlot = this._getSlot('customNavMenuBar');
        const countryPickerSlot = this._getSlot('countryPicker');

        const logoSlot = this._getSlot('logo');
        // @ts-ignore
        const logoImage  = logoSlot && logoSlot.length && logoSlot[0].props.config.image || {};
        // @ts-ignore
        const logoLink = logoSlot && logoSlot.length && logoSlot[0]?.props.config.imageLink || '';

        const viewProps: IHeaderViewProps = {
            ...(this.props as ICittaHeaderProps<ICittaHeaderData>),
            ...(this.state),
            logo: <Logo {...{ link: logoLink, image: logoImage, className: 'ms-header__logo', gridSettings: this.props.context.request.gridSettings, requestContext: this.props.context.request, telemetryContent: this.telemetryContent }} />,
            wishListIconDesktop: (
                <WishListIconComponent
                    className='ms-header__wishlist-desktop'
                    showButtonTooltip={true}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            wishListIconMobile: (
                <WishListIconComponent
                    className='ms-header__wishlist-mobile'
                    showButtonTooltip={true}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            cartIcon: (cartIconSlot && cartIconSlot.length > 0)
                ? cartIconSlot[0]
                : <CartIconComponent
                    cartLabel={cartLabel}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ cart: this.cart! }}
                    />,            
            navIcon: <NavIcon {...{ resources: this.props.resources, isExpanded: !this.state.mobileMenuCollapsed, targetId: navbarKey, toggleNavBar: this._toggleNavbar, telemetryContent: this.telemetryContent }} />,
            menuBar: menuBarSlot && menuBarSlot.length && menuBarSlot || [],
            search: searchSlot && searchSlot.length && searchSlot || [],
            siteOptions: siteOptionsSlot && siteOptionsSlot.length && siteOptionsSlot || [],
            categoryMenuBar: categoryMenuBarSlot && categoryMenuBarSlot.length && categoryMenuBarSlot || [],
            customNavBar: customNavBarSlot && customNavBarSlot.length && customNavBarSlot || [],
            className: headerClassName,
            countryPicker: countryPickerSlot && countryPickerSlot.length && countryPickerSlot || [],
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('ms-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-header__topbar')
            },
            HeaderTopBarRightContainer: {
                className: classnames('ms-header__topbar-right')
            },
            Divider: {
                className: classnames('ms-header__divider')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: `ms-header__citta-modal citta-header-mobile ${!this.state.mobileMenuCollapsed ? 'modal-open' : ''}`,
                toggle: this._toggleNavbar
            },
            MobileMenuHeader: (
                <ModalHeader
                    className='ms-header__mobile-hamburger-menu-header'
                    toggle={this._toggleNavbar}
                />
            ),
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body',
            },
            MobileMenuLinksContainer: {
                className: 'ms-header__mobile-hamburger-menu-links',
            },
            AccountInfoDropdownParentContainer: {
                className: `ms-header__account-info ${this.state.signinPopoverOpen ? 'account-open' : ''}`
            },
            AccountInfoDropdownPopoverConentContainer: customer && this.displayName && {
                // tag: Popover,
                id: 'myprofilePopover',
                className: 'ms-header__account-info-content',
                placement: 'bottom',
                isOpen: this.state.signinPopoverOpen,
                target: this.popOverRef,
                toggle: this._togglePopover,
            } || undefined,
            signInLink: this._getSigninButton(customer, user, this.props.resources),
            signOutLink: this._getSignOutButton(customer, user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(customer),
            accountLinks: this._getAccountLinks(customer),
            preferredStore: preferredStoreSlot
                && preferredStoreSlot.length
                && storeSelectorStateManager
                && (<HeaderPreferredStore {...this.props}/>)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* Handle link text change*/
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent) => {
        if (this.props.config.myAccountLinks && this.props.config.myAccountLinks[linkIndex]) {
            this.props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    }

    private _stickyHeaderSetup(): void {
        // Set header update methods to trigger on scroll
        window.onscroll = () => {
            this._updateHeader();
        };
        this._updateHeader();
    }

    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    private _getSigninButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: ICittaHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const isB2B = user && user.isB2b;
        if (!customer && isB2B) {
            window.location.href = '/';
            return undefined;
        }

        return !customer && (
            <Button
                className='ms-header__signin-button'
                title={resources.signInLinkText}
                href={user && user.signInUrl}
                aria-label={resources.signInLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden={true}>{resources.signInLinkText}</span>
            </Button>
        );
    }

    private _getSignOutButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: ICittaHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                className={'ms-header__signout-button'}
                title={resources.signOutLinkText}
                href={user && user.signOutUrl || ''}
                aria-label={resources.signOutLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign out')}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden={true}>{resources.signOutLinkText}</span>
            </Button>
        );
    }

    private _getAccountInfoDropdownButton(customer: Customer | undefined): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                innerRef={this.popOverRef}
                className={'ms-header__profile-button'}
                aria-describedby='myprofilePopover'
                onClick={this._togglePopover}
                color='link'
                aria-expanded={this.state.signinPopoverOpen}
                {...attributes}
            >
                <span className='ms-profile-button-text'>{this.displayName}</span>
                <span className='ms-profile-emarsys-email hide'>{this.accountEmailAddress}</span>
            </Button>
        );
    }

    private _getAccountLinks(customer: Customer | undefined): React.ReactNode[] | undefined {
        const { myAccountLinks } = this.props.config;
        const payLoad = getPayloadObject('click', this.telemetryContent, '');
        if(!customer || ! this.displayName || !myAccountLinks || !myAccountLinks.length) {
            return undefined;
        }
        return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
            payLoad.contentAction.etext = cta.linkText;
            const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: cta.ariaLabel,
                className: 'ms-signin-info__account-link-button',
                linkText: cta.linkText,
                linkUrl: cta.linkUrl.destinationUrl,
                openInNewTab: cta.openInNewTab,
                role: 'button',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={index}
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange(index), requestContext: this.props.context.request }}
                />
            );
        });
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
        this.state.mobileMenuCollapsed && this._setHeader(this.state.mobileMenuCollapsed);
    }

    private _togglePopover(): void {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    }

    private _keydown(e: KeyboardEvent): void {
        if (e.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
            this._setHeader(true);
        }
    }

    private _setHeader(lockHeader?: boolean): void {
         // Get heights of cookie and promotion banners
        const headerElem = document.querySelector('.ms-header');
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : 0;
        // triggers opacity change of header
        if (headerElem) {
            if (lockHeader || document.documentElement.scrollTop > bannerHeights) {
                headerElem.classList.add('lock-opaque');
            } else if (!window.document.body.classList.contains('search-open')) {  
                headerElem.classList.remove('lock-opaque');
            }
        }
    }

    private _updateHeader(isInMobileMenu?: boolean): void {
        // triggers opacity change of header
         // Get heights of cookie and promotion banners
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : 0;
        if (this.state.mobileMenuCollapsed) {
            this._setHeader();
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.ms-header__desktop-view');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__topbar-center');
        const headerLeftActionItem: HTMLElement | null = document.querySelector('.ms-header__topbar-left');
        const headerRightActionItem: HTMLElement | null = document.querySelector('.ms-header__topbar-right');

        if (stickyHeader && headerLogo && headerLeftActionItem && headerRightActionItem) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else  {
                stickyHeader.classList.remove('fixed');
            }

            // Fix edge sticky headers
            if (document.documentElement.scrollTop > bannerHeights) {
                headerLeftActionItem.classList.add('fixed');
                headerRightActionItem.classList.add('fixed');
            } else  {
                headerLeftActionItem.classList.remove('fixed');
                headerRightActionItem.classList.remove('fixed');
            }
        }
    }
}

export default Header;